import type { KeyboardEvent } from 'react';
import type { Account } from '../model/types';
import { Card } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Logo } from '@/shared/ui/logo';
import { Button } from '@/shared/ui/button';
import { ArrowLeft, Plus } from 'lucide-react';
import { useLanguage } from '@/shared/hooks/use-language';

interface AccountSelectorProps {
  accounts: Account[];
  selectedAccount: Account | null;
  onSelect: (account: Account) => void;
  onBack?: () => void;
  onExit?: () => void;
  onCreateAccount?: () => void;
  isLoading?: boolean;
}

function activateOnKey(event: KeyboardEvent<HTMLDivElement>, activate: () => void) {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }

  event.preventDefault();
  activate();
}

export function AccountSelector({
  accounts,
  selectedAccount,
  onSelect,
  onBack,
  onExit,
  onCreateAccount,
  isLoading = false
}: AccountSelectorProps) {
  const { t } = useLanguage();
  const activeAccounts = accounts.filter(account => account.status === 'active');

  const createAccountButton = onCreateAccount ? (
    <Button
      variant="outline"
      onClick={onCreateAccount}
      className="gap-2"
      data-testid="account-selection-create-account"
    >
      <Plus className="w-4 h-4" />
      {t("account.createAccount")}
    </Button>
  ) : null;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
        <div className="w-full max-w-2xl p-8">
          <div className="text-center">
            <Logo className="justify-center mb-4" />
            <p className="text-muted-foreground">{t("account.loading")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (activeAccounts.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn">
        <div className="w-full max-w-2xl p-8">
          <div className="text-center">
            <Logo className="justify-center mb-4" />
            <p className="text-muted-foreground">{t("account.noAccounts")}</p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              {createAccountButton}
              {onBack && (
                <Button variant="ghost" onClick={onBack} className="gap-2">
                  <ArrowLeft className="w-4 h-4" />
                  {t("account.backToHolderSelection")}
                </Button>
              )}
              {onExit && (
                <Button variant="ghost" onClick={onExit}>
                  {t("account.backToLogin")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn p-8">
      <div className="w-full max-w-2xl">
        <div className="mb-8 text-center">
          <Logo className="justify-center mb-4" />
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            {t("account.title")}
          </h1>
          <p className="text-muted-foreground">
            {t("account.subtitle")}
          </p>
        </div>

        {(onBack || onCreateAccount) && (
          <div className="mb-4 flex items-center justify-between">
            {onBack ? (
              <Button
                variant="ghost"
                onClick={onBack}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {t("account.back")}
              </Button>
            ) : (
              <span />
            )}
            {createAccountButton}
          </div>
        )}
        
        <div className="space-y-4">
          {activeAccounts.map((account) => (
            <Card
              key={account.id}
              role="button"
              tabIndex={0}
              aria-pressed={selectedAccount?.id === account.id}
              onClick={() => onSelect(account)}
              onKeyDown={(event) => activateOnKey(event, () => onSelect(account))}
              className={`bg-card border-border p-6 cursor-pointer transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background ${
                selectedAccount?.id === account.id 
                  ? 'border-primary border-2 shadow-lg' 
                  : 'card-hover'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-xl font-semibold text-foreground">
                      {account.name}
                    </h3>
                    <Badge className="bg-green-500/10 text-green-500">
                      {t("account.status")}
                    </Badge>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
